/**
 * Main export orchestration
 *
 * Creates an Excel workbook with all sheets in the correct order:
 * 1. RFP Details
 * 2. RFP Notes
 * 3. Classes
 * 4. LOC sheets (benefits + rates + summary)
 */

import ExcelJS from "exceljs";
import { logger } from "@platform/browser-logger";
import { GridApi } from "ag-grid-community";
import { sortByCoverage } from "@platform/types";
import { coverageNoteQueryKeys, resolveNoteKey } from "@platform/hooks";
import {
  coverageTypeToLabel,
  getCoverageNote,
  normalizeNoteBenefitPlanType,
} from "@platform/plan-grid-core";

import { addRfpDetailsWorksheet } from "./rfpDetails";
import { addNotesWorksheet } from "./notes";
import { addCoverageNotesToWorksheet } from "./coverageNotes";
import { addClassesWorksheet } from "./classes";
import { buildBenefitsWorksheet } from "./benefits";
import { mergePlanSpanCells } from "./worksheetLayout";
import {
  extractSummaryRows,
  appendSummaryToWorksheet,
  ExcelRow,
} from "./rates";
import { GridType, CoverageExportData } from "./types";

import type { CellValue } from "./types";
import type { AxiosInstance } from "axios";
import type { QueryClient } from "@tanstack/react-query";

interface ExportOrchestratorParams {
  coveragesData: CoverageExportData[];
  gridApis: Map<string, GridApi>;
  ratesSheetData: Map<string, { rows: ExcelRow[] }>;
  rfpName: string;
  ecoRfpId: string;
  brokerPortalRfpId: string;
  axiosInstance: AxiosInstance;
  queryClient?: Pick<QueryClient, "getQueryData">;
}

const getGridKey = (coverageType: string, gridType: GridType): string =>
  `${coverageType}-${gridType}`;

const getCachedBaselineCoverageNote = (
  queryClient: Pick<QueryClient, "getQueryData"> | undefined,
  ecoRfpId: string,
  coverageId: string,
  benefitPlanType: string | undefined,
): { hasCachedBaselineNote: boolean; cachedBaselineNote: string } => {
  const cachedBaselineNotes = queryClient?.getQueryData<Record<string, string>>(
    coverageNoteQueryKeys.baselineCoverageNotes(ecoRfpId),
  );

  const noteKey = resolveNoteKey({ id: coverageId, benefitPlanType });

  if (!cachedBaselineNotes || !(noteKey in cachedBaselineNotes)) {
    return {
      hasCachedBaselineNote: false,
      cachedBaselineNote: "",
    };
  }

  return {
    hasCachedBaselineNote: true,
    cachedBaselineNote: cachedBaselineNotes[noteKey] ?? "",
  };
};

const addCoverageNotesSection = async (
  worksheet: ExcelJS.Worksheet,
  data: CoverageExportData,
  ecoRfpId: string,
  axiosInstance: AxiosInstance,
  queryClient?: Pick<QueryClient, "getQueryData">,
): Promise<void> => {
  const benefitPlanType = normalizeNoteBenefitPlanType(
    data.coverage.productType ?? data.benefitPlanGroups[0]?.productType,
  );

  const { hasCachedBaselineNote, cachedBaselineNote } =
    getCachedBaselineCoverageNote(
      queryClient,
      ecoRfpId,
      data.coverage.id,
      benefitPlanType,
    );

  if (hasCachedBaselineNote) {
    if (cachedBaselineNote) {
      logger.info(
        `[btx export] Adding cached coverage notes for: ${data.coverage.type}`,
      );
      addCoverageNotesToWorksheet(worksheet, cachedBaselineNote);
    }

    return;
  }

  logger.info(
    `[btx export] Fetching coverage notes for: ${data.coverage.type}`,
  );
  const noteResponse = await getCoverageNote(
    ecoRfpId,
    data.coverage.id,
    axiosInstance,
    benefitPlanType,
  );

  if (!noteResponse.content) {
    return;
  }

  logger.info(`[btx export] Adding coverage notes for: ${data.coverage.type}`);
  addCoverageNotesToWorksheet(worksheet, noteResponse.content);
};

/**
 * Build complete Excel workbook with all sheets in correct order
 */
export async function buildExportWorkbook(
  params: ExportOrchestratorParams,
): Promise<ExcelJS.Workbook> {
  const {
    coveragesData,
    gridApis,
    ratesSheetData,
    ecoRfpId,
    brokerPortalRfpId,
    axiosInstance,
    queryClient,
  } = params;
  const workbook = new ExcelJS.Workbook();

  // 1. Add RFP Details
  logger.info("[btx export] Adding RFP Details worksheet");
  await addRfpDetailsWorksheet(workbook, brokerPortalRfpId, axiosInstance);

  // 2. Add RFP Notes
  logger.info("[btx export] Adding RFP Notes worksheet");
  await addNotesWorksheet(workbook, ecoRfpId, axiosInstance);

  // 3. Add Classes
  logger.info("[btx export] Adding Classes worksheet");
  const allCoverages = coveragesData.map((data) => ({
    id: data.coverage.id,
    type: data.coverage.type,
    createdAt: data.coverage.createdAt,
    updatedAt: data.coverage.updatedAt,
  }));
  await addClassesWorksheet(workbook, ecoRfpId, allCoverages, axiosInstance);

  // 4. Add LOC sheets (sorted by coverage order)
  const sortedCoveragesData = sortByCoverage(coveragesData);

  for (const data of sortedCoveragesData) {
    const coverageLabel = coverageTypeToLabel(data.coverage.type);
    logger.info(`[btx export] Adding LOC sheet: ${coverageLabel}`);

    // Get benefits grid API
    const benefitsKey = getGridKey(data.coverage.type, GridType.Benefits);
    const benefitsApi = gridApis.get(benefitsKey);

    if (!benefitsApi) {
      logger.warn(
        `[btx export] No benefits API found for: ${data.coverage.type}`,
      );
      continue;
    }

    // Create worksheet and build benefits section
    const worksheet = workbook.addWorksheet(coverageLabel);

    const planLayout = buildBenefitsWorksheet(
      worksheet,
      benefitsApi,
      data.coverage.type,
    );

    // Add rates section if available
    const hasRatesData =
      data.benefitPlanGroups && data.benefitPlanGroups.length > 0;
    if (hasRatesData) {
      const ratesData = ratesSheetData.get(data.coverage.type);

      if (ratesData?.rows && ratesData.rows.length > 0) {
        logger.info(`[btx export] Adding rates for: ${data.coverage.type}`);

        const { planIdsInOrder, planColumnSpans } = planLayout;

        // Add rates header
        worksheet.addRow(["Rates"]);

        // Add rates data
        ratesData.rows.forEach((row) => {
          const rowData: CellValue[] = [row.label];
          planIdsInOrder.forEach((planId) => {
            const span = planColumnSpans[planId] ?? 1;
            rowData.push(row.values[planId] ?? "");

            for (let index = 1; index < span; index += 1) {
              rowData.push("");
            }
          });
          const addedRow = worksheet.addRow(rowData);
          mergePlanSpanCells(
            worksheet,
            addedRow.number,
            planIdsInOrder,
            planColumnSpans,
          );
          addedRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
            cell.alignment = { horizontal: "left", vertical: "middle" };
            if (colNumber > 1) {
              cell.border = {
                top: { style: "thin" },
                bottom: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" },
              };
            }
          });
        });

        // Add summary section
        const summaryKey = getGridKey(data.coverage.type, GridType.Summary);
        const summaryApi = gridApis.get(summaryKey);

        if (summaryApi) {
          logger.info(`[btx export] Adding summary for: ${data.coverage.type}`);
          const summaryRows = extractSummaryRows(summaryApi);
          const planGroupIdByPlanId: Record<string, string> = {};
          data.benefitPlanGroups.forEach((group) => {
            group.benefitPlans.forEach((plan) => {
              planGroupIdByPlanId[plan.id] = group.id;
            });
          });
          appendSummaryToWorksheet(
            worksheet,
            summaryRows,
            planIdsInOrder,
            planColumnSpans,
            planGroupIdByPlanId,
          );
        }
      }
    }

    // Add coverage notes section
    try {
      await addCoverageNotesSection(
        worksheet,
        data,
        ecoRfpId,
        axiosInstance,
        queryClient,
      );
    } catch (error) {
      logger.error(
        `[btx export] Failed to fetch coverage notes for: ${data.coverage.type}`,
        error as Error,
      );
      // Continue with export even if notes fail
    }
  }

  return workbook;
}
